import { X } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BrowserRouter as Router } from "react-router-dom";

import { API_V3_BASE_URL } from "@/api-v3";
import { useBootstrap } from "@/features/bootstrap";
import {
  APP_NAME,
  brandingAssetUrl,
  useTenantDisplayName,
} from "@/features/branding";
import { ENV } from "@/features/config";
import StorageManager from "@/features/storage";
import { useToast } from "@/hooks/use-toast";

import { PWAInstallComponent } from "./components/pwa-install-component";
import { ToastAction } from "./components/ui/toast";
import { CustomErrorBoundary } from "./features/error-boundary";
import {
  GlobalLanguageSelectorDialog,
  getLocalizedMessage,
} from "./features/localisation";
import { AppRoutes } from "./router/app-routes";

const App = () => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const message = getLocalizedMessage(ENV.APP.GLOBAL_MESSAGE);
  const clientName = useTenantDisplayName() ?? APP_NAME;
  const { data: bootstrap } = useBootstrap();

  // Title stays the index.html fallback ("Participants App") until the
  // /bootstrap response actually arrives, then update the title.
  useEffect(() => {
    if (bootstrap) {
      document.title = bootstrap.display_name || bootstrap.tenant_name;
    }
  }, [bootstrap]);

  useEffect(() => {
    if (message && !StorageManager.didDisplayGlobalMessage()) {
      toast({
        description: message,
        duration: undefined,
        action: (
          <ToastAction
            altText="Close"
            onClick={() => {
              StorageManager.setDisplayedGlobalMessage();
            }}
          >
            <X />
          </ToastAction>
        ),
      });
    }
  }, [message, toast]);

  return (
    <Router>
      <CustomErrorBoundary>
        <AppRoutes />
        <GlobalLanguageSelectorDialog />
      </CustomErrorBoundary>

      {/* NOTE: While description is disabled the dialog falls back to the default text that is in the manifest this makes the translation not working.
        Also if we set the disableDescription to false, the installDescription is forced.*/}
      <PWAInstallComponent
        name={clientName}
        disableDescription={true}
        disableScreenshots={true}
        description={t("pwa_install_description", {
          client: clientName,
        })}
        // installDescription={" "}
        icon={brandingAssetUrl("icon-512")}
        manifestUrl={`${API_V3_BASE_URL}/branding/manifest.webmanifest`}
      />
    </Router>
  );
};

export default App;
